import * as React from 'react';
import type { FormikProps } from 'formik';
import Select from 'react-select';
import type { Country } from 'core/utils/types/modules/org';
import { GENDERS } from 'core/utils/constants';
import { useFetchCountryCode } from 'features/BranchSetup/hooks/useFetchCountryCode';
import {
  DMDiv,
  DMInput,
  DMLabel,
  DMSelect,
  DMText,
  DMOption,
} from './Primitives';

type FieldNames = {
  gender: string;
  countryCode: string;
  mobile: string;
};

type EditWebsiteContentFieldProps<TValues extends Record<string, any>> = {
  formik: FormikProps<TValues>;
  names: FieldNames;
  inputClassName: string;
  labelClassName: string;
  errorClassName: string;
  className?: string;
  countryCodeClassName?: string;
};

const toGenderOptionLabel = (label: string) =>
  label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();

const getFieldError = <TValues extends Record<string, any>>(
  formik: FormikProps<TValues>,
  name: string
) => {
  const touched = formik.touched as Record<string, any>;
  const errors = formik.errors as Record<string, any>;

  return touched[name] && errors[name] ? String(errors[name]) : '';
};

export function EditWebsiteContentGenderField<
  TValues extends Record<string, any>
>({
  formik,
  names,
  inputClassName,
  labelClassName,
  errorClassName,
}: EditWebsiteContentFieldProps<TValues>) {
  const error = getFieldError(formik, names.gender);

  return (
    <DMDiv className="col-span-3">
      <DMLabel className={labelClassName}>
        Gender
        <DMText as="span" className="ml-[0.25vw] text-red-500">
          *
        </DMText>
      </DMLabel>
      <DMSelect
        name={names.gender}
        value={formik.values[names.gender] || ''}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        className={inputClassName}
      >
        <DMOption value="">Select Gender</DMOption>
        {GENDERS.map(gender => {
          const label = toGenderOptionLabel(gender.label);

          return (
            <DMOption key={gender.value} value={label}>
              {label}
            </DMOption>
          );
        })}
      </DMSelect>
      {error ? <DMText className={errorClassName}>{error}</DMText> : null}
    </DMDiv>
  );
}

export function EditWebsiteContentMobileField<
  TValues extends Record<string, any>
>({
  formik,
  names,
  inputClassName,
  labelClassName,
  errorClassName,
  className,
  countryCodeClassName,
}: EditWebsiteContentFieldProps<TValues>) {
  const [countryList, setCountryList] = React.useState<Country[]>([]);

  useFetchCountryCode(countryList, setCountryList);

  const countryCodeError = getFieldError(formik, names.countryCode);
  const mobileError = getFieldError(formik, names.mobile);
  const countryOptions = countryList.length
    ? countryList
    : [{ COUNTRY_CODE: '+91' } as Country];
  const selectedCountry =
    countryOptions.find(
      country =>
        country.COUNTRY_CODE === (formik.values[names.countryCode] || '+91')
    ) ||
    countryOptions.find(country => country.COUNTRY_CODE === '+91') ||
    countryOptions[0];
  const hasError = !!countryCodeError || !!mobileError;

  return (
    <DMDiv className={className ?? 'col-span-3'}>
      <DMLabel className={labelClassName}>
        Mobile Number
        <DMText as="span" className="ml-[0.25vw] text-red-500">
          *
        </DMText>
      </DMLabel>
      <DMDiv
        className={`flex h-[6.8vh] w-full rounded-[8px] border bg-white ${
          hasError
            ? 'border-red-400'
            : 'border-gray-300 focus-within:border-primary-main'
        }`}
      >
        <DMDiv
          className={`${
            countryCodeClassName || 'w-[8.2vw]'
          } shrink-0 border-r border-gray-300`}
        >
          <Select
            name={names.countryCode}
            value={selectedCountry}
            options={countryOptions}
            getOptionValue={(option: Country) => option.COUNTRY_CODE}
            getOptionLabel={(option: Country) => option.COUNTRY_CODE}
            onChange={(country: any) => {
              formik.setFieldValue(
                names.countryCode,
                country?.COUNTRY_CODE || '+91'
              );
              formik.setFieldTouched(names.countryCode, true, false);
            }}
            onBlur={() => formik.setFieldTouched(names.countryCode, true)}
            isSearchable
            classNamePrefix="dm-country-code"
            styles={{
              control: base => ({
                ...base,
                minHeight: '100%',
                height: '6.8vh',
                border: 0,
                boxShadow: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
              }),
              valueContainer: base => ({
                ...base,
                height: '6.8vh',
                padding: '0 0.55vw',
              }),
              input: base => ({
                ...base,
                margin: 0,
                padding: 0,
                color: '#111827',
                fontSize: '1.75vh',
                fontWeight: 500,
              }),
              singleValue: base => ({
                ...base,
                color: '#111827',
                fontSize: '1.75vh',
                fontWeight: 500,
              }),
              indicatorsContainer: base => ({
                ...base,
                height: '6.8vh',
              }),
              indicatorSeparator: () => ({ display: 'none' }),
              dropdownIndicator: base => ({
                ...base,
                padding: '0 0.45vw 0 0',
                color: '#6b7280',
              }),
              menu: base => ({
                ...base,
                zIndex: 20,
                fontSize: '1.65vh',
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected
                  ? '#0f3f46'
                  : state.isFocused
                  ? '#f3f4f6'
                  : '#fff',
                color: state.isSelected ? '#fff' : '#111827',
                cursor: 'pointer',
              }),
            }}
          />
        </DMDiv>
        <DMInput
          name={names.mobile}
          value={formik.values[names.mobile] || ''}
          onChange={event => {
            const onlyNumbers = event.target.value.replace(/\D/g, '');
            formik.setFieldValue(names.mobile, onlyNumbers);
          }}
          onBlur={formik.handleBlur}
          placeholder="Enter Mobile Number"
          inputMode="numeric"
          className="min-w-0 flex-1 border-0 bg-transparent px-[1.15vw] text-[1.95vh] font-medium text-gray-950 outline-none placeholder:text-gray-400"
        />
      </DMDiv>
      {countryCodeError ? (
        <DMText className={errorClassName}>{countryCodeError}</DMText>
      ) : null}
      {mobileError ? (
        <DMText className={errorClassName}>{mobileError}</DMText>
      ) : null}
    </DMDiv>
  );
}
